import { ReactNode, useCallback, useEffect, useState } from 'react';
import { DesignHeader } from './DesignHeader';
import { DesignSidebar } from './DesignSidebar';
import { SettingsPanel } from './SettingsPanel';

const USER = { name: 'Demo User', role: 'Warranty Administrator', initials: 'DU' };

/** Matches the Tailwind `lg` breakpoint, which this project overrides to MUI's 1200px. */
const RAIL_BELOW = 1200;
/** Below `md` (900px) the rail floats over the content instead of displacing it. */
const OVERLAY_BELOW = 900;

type AppFrameProps = {
  context?: string;
  children: ReactNode;
};

/**
 * Shared demo shell: header + sidebar + settings panel + scrolling main.
 *
 * The responsive rules fix a real bug in the production shell: there the JS
 * threshold is 1024px while the content offset uses `lg:`, which this project
 * overrides to 1200px — so between 1024 and 1200 the sidebar opens and
 * overlaps the page. Here one constant drives both, and it is read from a
 * resize listener rather than only on route change.
 */
export const AppFrame = ({ context, children }: AppFrameProps) => {
  const [activePath, setActivePath] = useState('/warranty');
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [width, setWidth] = useState(() => window.innerWidth);
  const [userCollapsed, setUserCollapsed] = useState<boolean | null>(null);

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isOverlay = width < OVERLAY_BELOW;
  // An explicit toggle always wins; otherwise the viewport decides.
  const collapsed = userCollapsed ?? width < RAIL_BELOW;

  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  return (
    <div className="ds-shell">
      <DesignHeader
        context={context}
        onToggleSidebar={() => setUserCollapsed((c) => !(c ?? width < RAIL_BELOW))}
        onOpenSettings={openSettings}
      />

      <div className="ds-body-row">
        {/* On tablet portrait the expanded rail floats, so it needs a scrim
            to make "tap anywhere to dismiss" discoverable. */}
        {isOverlay && !collapsed && (
          <div
            className="ds-scrim"
            onMouseDown={() => setUserCollapsed(true)}
            aria-hidden="true"
          />
        )}

        <DesignSidebar
          activePath={activePath}
          onNavigate={(p) => {
            setActivePath(p);
            if (isOverlay) setUserCollapsed(true);
          }}
          collapsed={collapsed}
          overlay={isOverlay}
          user={USER}
          onOpenSettings={openSettings}
        />

        <main className="ds-main ds-scroll" id="ds-main">
          {children}
        </main>
      </div>

      <SettingsPanel open={settingsOpen} onClose={closeSettings} />
    </div>
  );
};
